// The groove catalogue: grooves authored as DATA rather than code.
//
// `STYLE_PRESETS` (src/music/style.ts) is the built-in baseline — eight feels
// compiled into the bundle. This module is the extensible layer beside it:
// every `*.json` in `src/data/catalogue/grooves/` is a `GroovePresetDoc`, loaded and
// validated by `bundledDocuments()`, so adding a groove is a data change the
// dev writer endpoint can make — no code edit, no rebuild of the picker.
//
// The important piece here is NOT the loader, it is `bandToGrooveState` /
// `grooveStateToBand`: the one bridge between the live mixer band (`StyleBand`,
// what the app plays) and the document vocabulary (`GrooveState`, what a
// catalogue groove stores). Without a single bridge the two vocabularies drift
// and every consumer grows its own half-correct mapping — the duplication this
// whole storage slice exists to avoid.

import type { ChannelSource, GrooveState } from '@/music/arrangement';
import { COMP_STYLES, type CompStyle } from '@/music/comping';
import { compFeelForBand, type StyleBand } from '@/music/style';
import type { BassFeel, BassStyle } from '@/music/walkingBass';
import {
  DEFAULT_SWING_FEEL,
  normalizeSwingOffset,
  SWING_FEELS,
  swingGrid,
  type SwingFeel,
} from '@/music/swingFeel';
import {
  BASS_ACCENTS,
  MELODY_PHRASINGS,
  PLACEMENTS,
  STRAIGHT_BAND_FEEL,
  SWING_BAND_FEEL,
  type BassAccent,
  type MelodyPhrasing,
  type Placement,
} from '@/music/bandFeel';
import { bundledDocuments } from './bundledDocuments';
import type { GroovePresetDoc, TuneArrangementDoc } from './documents';

/** Generator settings a `{kind:'generated'}` bass channel carries. */
export interface BassChannelConfig {
  style: BassStyle;
  feel: BassFeel;
  swing: boolean;
  ghosts: boolean;
  anticipate: boolean;
  triplets: boolean;
  skips: boolean;
  amount: number;
  placement: Placement;
  accent: BassAccent;
  /** How much the line swings against the band under the Kit choice; absent = 0. */
  swingOffset?: number;
}

/** Generator settings a `{kind:'generated'}` comp channel carries. */
export interface CompChannelConfig {
  style: CompStyle;
  amount: number;
  placement: Placement;
  /** How much the comp swings against the band under the Kit choice; absent = 0. */
  swingOffset?: number;
}

/** How the generated melody channel plays the written line. */
export interface LeadChannelConfig {
  placement: Placement;
  phrasing: MelodyPhrasing;
  /** How much the melody swings against the band under the Kit choice; absent = 0. */
  swingOffset?: number;
}

/** Each swing feel's ratio as an exact fraction of the pair the first note takes. */
const FEEL_RATIO: Record<SwingFeel, { num: number; den: number }> = {
  'triplet-8th': { num: 2, den: 3 },
  'triplet-16th': { num: 2, den: 3 },
  'dotted-8th': { num: 3, den: 4 },
  'dotted-16th': { num: 3, den: 4 },
  'scottish-8th': { num: 1, den: 4 },
  'scottish-16th': { num: 1, den: 4 },
};

/** The swing feel a document's feel names; a ratio we do not offer reads as
 *  the triplet at its level. */
function feelFromState(feel: GrooveState['feel'], fallback: SwingFeel): SwingFeel {
  if (feel.subdivision !== 'swing') return fallback;
  const sixteenth = feel.unit === 'sixteenth';
  const ratio = feel.ratio && feel.ratio.den ? feel.ratio.num / feel.ratio.den : 2 / 3;
  const match = SWING_FEELS.find(
    (id) => id.endsWith('16th') === sixteenth && Math.abs(swingGrid(id).ratio - ratio) < 1e-6,
  );
  return match ?? (sixteenth ? 'triplet-16th' : DEFAULT_SWING_FEEL);
}

/**
 * The live band as a groove document's state.
 *
 * Channel enablement is expressed in the channel vocabulary — a muted comp is
 * `{kind:'off'}`, silent drums are `{kind:'off'}` — which means an OFF channel
 * carries no generator settings. That is deliberate: a groove that says "no
 * bass" should not also smuggle in bass settings nobody can hear. The reverse
 * conversion takes those from a base band (see `grooveStateToBand`).
 *
 * `lead` is always the generated melody channel: whether the melody sounds is
 * a transport setting (`melodyChannel`), not part of a groove.
 */
export function bandToGrooveState(band: StyleBand): GrooveState {
  const bassConfig: BassChannelConfig = {
    style: band.bassStyle,
    feel: band.bassFeel,
    swing: band.bassSwing,
    ghosts: band.bassGhosts,
    anticipate: band.bassAnticipate,
    triplets: band.bassTriplets,
    skips: band.bassSkips,
    amount: band.bassAmount,
    placement: band.bassPlacement,
    accent: band.bassAccent,
    ...offsetField(band.bassSwingOffset),
  };
  // Interactive over the funk kit uses exactly the Funk generator. Store
  // that explicit rhythm so muting (and therefore omitting) the drum channel
  // cannot turn it back into swing when loaded against another base band.
  const compConfig: CompChannelConfig = {
    style:
      band.compStyle === 'interactive' && compFeelForBand(band) === 'funk'
        ? 'funk'
        : band.compStyle,
    amount: band.compAmount,
    placement: band.compPlacement,
    ...offsetField(band.compSwingOffset),
  };
  const leadConfig: LeadChannelConfig = {
    placement: band.melodyPlacement,
    phrasing: band.melodyPhrasing,
    ...offsetField(band.melodySwingOffset),
  };
  return {
    feel: band.bassSwing
      ? {
          subdivision: 'swing',
          ratio: { ...FEEL_RATIO[band.swingFeel ?? DEFAULT_SWING_FEEL] },
          ...((band.swingFeel ?? DEFAULT_SWING_FEEL).endsWith('16th')
            ? { unit: 'sixteenth' as const }
            : {}),
          // The kit is the default: only a ratio held whatever the kit plays
          // is written down.
          ...(band.swingWithKit === false ? { withKit: false } : {}),
          // How far past the kit it swings, likewise only when it does.
          ...(band.swingOffset ? { kitOffset: band.swingOffset } : {}),
        }
      : { subdivision: 'straight' },
    lead: { kind: 'generated', config: { ...leadConfig } },
    comp: band.bassSolo ? { kind: 'off' } : { kind: 'generated', config: { ...compConfig } },
    bass: band.bassline ? { kind: 'generated', config: { ...bassConfig } } : { kind: 'off' },
    drums: band.drums ? { kind: 'loop', loopKey: band.drumLoopKey } : { kind: 'off' },
  };
}

/**
 * A groove document's state as a live band, filling anything the state does
 * not carry from `base` (normally the band currently loaded).
 *
 * Every field is read defensively: a document is untrusted input, and a groove
 * with a nonsense bass style must degrade to the base's setting rather than
 * reach the generator.
 */
export function grooveStateToBand(state: GrooveState, base: StyleBand): StyleBand {
  const bass = configOf(state.bass);
  const comp = configOf(state.comp);
  const lead = configOf(state.lead);
  const bassSwing = bool(bass.swing, state.feel.subdivision === 'swing');
  // A document written before the feel fields existed plays its own kind's
  // defaults (a swing groove laid back and phrased, a Latin one centred and
  // as written), not whatever the base band happens to carry.
  const feel = bassSwing ? SWING_BAND_FEEL : STRAIGHT_BAND_FEEL;
  return {
    bassline: state.bass.kind !== 'off',
    bassSolo: state.comp.kind === 'off',
    bassStyle: pick(bass.style, BASS_STYLE_VALUES, base.bassStyle),
    bassFeel: pick(bass.feel, BASS_FEEL_VALUES, base.bassFeel),
    // The heads-in-2 convention is the PLAYER's setting, not a groove's: a
    // groove document describes a feel, not an itinerary, so it follows base.
    bassFollowForm: base.bassFollowForm,
    // Likewise the comp INSTRUMENT: a groove describes a rhythmic feel, and
    // which pair of hands plays it is the player's choice, not the groove's.
    // The piano voicing family rides with it for the same reason.
    compInstrument: base.compInstrument,
    compPianoFamily: base.compPianoFamily,
    bassSwing,
    swingFeel: feelFromState(state.feel, base.swingFeel ?? DEFAULT_SWING_FEEL),
    swingWithKit: state.feel.withKit !== false,
    melodyPlacement: pick(lead.placement, PLACEMENTS, feel.melodyPlacement),
    melodyPhrasing: pick(lead.phrasing, MELODY_PHRASINGS, feel.melodyPhrasing),
    bassPlacement: pick(bass.placement, PLACEMENTS, feel.bassPlacement),
    bassAccent: pick(bass.accent, BASS_ACCENTS, feel.bassAccent),
    bassSkips: bool(bass.skips, feel.bassSkips),
    compPlacement: pick(comp.placement, PLACEMENTS, feel.compPlacement),
    swingOffset: normalizeSwingOffset(state.feel.kitOffset) ?? feel.swingOffset,
    melodySwingOffset: normalizeSwingOffset(lead.swingOffset) ?? feel.melodySwingOffset,
    compSwingOffset: normalizeSwingOffset(comp.swingOffset) ?? feel.compSwingOffset,
    bassSwingOffset: normalizeSwingOffset(bass.swingOffset) ?? feel.bassSwingOffset,
    bassGhosts: bool(bass.ghosts, base.bassGhosts),
    bassAnticipate: bool(bass.anticipate, base.bassAnticipate),
    bassTriplets: bool(bass.triplets, base.bassTriplets),
    bassAmount: amount(bass.amount, base.bassAmount),
    compStyle: pick(comp.style, COMP_STYLE_VALUES, base.compStyle),
    compAmount: amount(comp.amount, base.compAmount),
    drums: state.drums.kind === 'loop',
    drumLoopKey: state.drums.kind === 'loop' ? state.drums.loopKey : base.drumLoopKey,
  };
}

const BASS_STYLE_VALUES: readonly BassStyle[] = [
  'roots',
  'roots-fifths',
  'chromatic',
  'dominant',
  'scale',
  'walking',
  'advanced',
  'shuffle',
  'latin',
];
const BASS_FEEL_VALUES: readonly BassFeel[] = ['two', 'four'];
const COMP_STYLE_VALUES = COMP_STYLES.map(({ id }) => id);

/** A part's swing offset as a config field, written only when it is not 0. */
function offsetField(offset: number | undefined): { swingOffset?: number } {
  return offset ? { swingOffset: offset } : {};
}

function configOf(channel: ChannelSource): Record<string, unknown> {
  return channel.kind === 'generated' && channel.config ? channel.config : {};
}

function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function amount(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

// --- the catalogue ----------------------------------------------------------

/**
 * Every valid groove document, sorted by category then name. Loading and
 * validation are `bundledDocuments()`'s job — this is only the groove view of
 * the catalogue.
 */
export function grooveCatalogue(): GroovePresetDoc[] {
  return bundledDocuments()
    .filter((doc): doc is GroovePresetDoc => doc.kind === 'groove')
    .sort(
      (a, b) => (a.category ?? '').localeCompare(b.category ?? '') || a.name.localeCompare(b.name),
    );
}

/**
 * The catalogue's DEFAULT arrangement for a score, if one ships.
 *
 * A curated tune can arrive already knowing how it plays its own written
 * feels — Night's `Latin` means the Afro-Cuban groove, not "whatever band is
 * loaded". This is only ever a default: the user's own assignment wins, and
 * the state here is a COPY, so editing the source groove later cannot reach a
 * tune somebody has already arranged.
 */
export function catalogueArrangementFor(
  scoreId: string | undefined,
): TuneArrangementDoc | undefined {
  if (!scoreId) return undefined;
  return bundledDocuments().find(
    (doc): doc is TuneArrangementDoc => doc.kind === 'arrangement' && doc.scoreId === scoreId,
  );
}

/** The catalogue's Feel → band defaults for a score, in the mixer vocabulary. */
export function catalogueFeelBands(
  scoreId: string | undefined,
  base: StyleBand,
): Record<string, StyleBand> {
  const arrangement = catalogueArrangementFor(scoreId);
  if (!arrangement) return {};
  const out: Record<string, StyleBand> = {};
  for (const [feel, snapshot] of Object.entries(arrangement.feels)) {
    out[feel] = grooveStateToBand(snapshot.state, base);
  }
  return out;
}
